import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ErrorDisplay = ({ error, onRetry, onViewDashboard }) => {
  const errorTypes = {
    compression: {
      icon: "AlertCircle",
      title: "Compression Failed",
      message: "Unable to compress project files. Please try again.",
      color: "text-error"
    },
    upload: {
      icon: "CloudOff",
      title: "Upload Failed",
      message: "Failed to upload to storage. Check your connection and retry.",
      color: "text-warning"
    },
    network: {
      icon: "WifiOff",
      title: "Network Error",
      message: "Connection lost. Please check your internet and try again.",
      color: "text-error"
    },
    storage: {
      icon: "HardDrive",
      title: "Storage Error",
      message: "Insufficient storage space. Please free up space and retry.",
      color: "text-warning"
    },
    default: {
      icon: "XCircle",
      title: "Download Failed",
      message: "An unexpected error occurred. Please try again.",
      color: "text-error"
    }
  };

  const errorInfo = errorTypes?.[error?.type] || errorTypes?.default;

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 border border-border">
      <div className="flex flex-col items-center text-center space-y-4 md:space-y-6">
        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-error/10 flex items-center justify-center ${errorInfo?.color}`}>
          <Icon name={errorInfo?.icon} size={32} className="md:w-10 md:h-10" />
        </div>

        <div className="space-y-2">
          <h3 className="text-lg md:text-xl font-semibold">{errorInfo?.title}</h3>
          <p className="text-sm md:text-base text-muted-foreground max-w-md">
            {error?.message || errorInfo?.message}
          </p>
        </div>

        {error?.details && (
          <div className="w-full p-3 md:p-4 bg-muted rounded-md text-left">
            <p className="text-xs md:text-sm font-mono text-muted-foreground break-all">
              {error?.details}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 w-full">
          <Button
            variant="default"
            fullWidth
            iconName="RefreshCw"
            iconPosition="left"
            onClick={onRetry}
          >
            Retry Download
          </Button>
          <Button
            variant="outline"
            fullWidth
            iconName="Folder"
            iconPosition="left"
            onClick={onViewDashboard}
          >
            View Dashboard
          </Button>
        </div>

        <div className="pt-4 border-t border-border w-full">
          <h4 className="text-sm md:text-base font-semibold mb-3">Alternative Access:</h4>
          <div className="space-y-2">
            <div className="flex items-start gap-3 p-3 bg-muted rounded-md">
              <Icon name="History" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-medium">Project History</p>
                <p className="text-xs text-muted-foreground">
                  Access this project from your dashboard anytime
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted rounded-md">
              <Icon name="Mail" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-medium">Email Delivery</p>
                <p className="text-xs text-muted-foreground">
                  Request project files via email as backup
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;